import Vue from 'vue';
import Router from 'vue-router';
Vue.use(Router);

/* Layout */
import Layout from '@/layout/index.vue';
import Welcome from '@/views/_dev/Welcome.vue';
import cognito from '@/cognito';

import { Message } from 'element-ui';
import i18n from '@/i18n'; // internationalization
import store from '@/store';

import getUserInfo from '@/cognito/getUserInfo';
import { loadAndStorePermissions, loadAndStoreUserAndParticipants } from '@/helpers/permission-helper';

import apolloClient from '@/apollo/client';

/* Router Modules */
// import componentsRouter from './modules/components';
// import chartsRouter from './modules/charts';
// import tableRouter from './modules/table';
// import nestedRouter from './modules/nested';

/**
 * Note: sub-menu only appear when route children.length >= 1
 * Detail see: https://panjiachen.github.io/vue-element-admin-site/guide/essentials/router-and-nav.html
 *
 * hidden: true                   if set true, item will not show in the sidebar(default is false)
 * alwaysShow: true               if set true, will always show the root menu
 *                                if not set alwaysShow, when item has more than one children route,
 *                                it will becomes nested mode, otherwise not show the root menu
 * redirect: noRedirect           if set noRedirect will no redirect in the breadcrumb
 * name:'router-name'             the name is used by <keep-alive> (must set!!!)
 * meta : {
roles: ['admin','editor']    control the page roles (you can set multiple roles)
title: 'title'               the name show in sidebar and breadcrumb (recommend set)
icon: 'svg-name'             the icon show in the sidebar
noCache: true                if set true, the page will no be cached(default is false)
affix: true                  if set true, the tag will affix in the tags-view
breadcrumb: false            if set false, the item will hidden in breadcrumb(default is true)
activeMenu: '/example/list'  if set path, the sidebar will highlight the path you set
}
*/

/**
 * constantRoutes
 * a base page that does not have permission requirements
 * all roles can be accessed
 */

async function LoadPermissionsIfNeeded(store) {
	const licenses = store.state.risolto.licences;
	if (licenses.length > 0) {
		return;
	}

	const { data } = await getUserInfo();

	await loadAndStorePermissions(apolloClient, data.sub, store);
}

async function loadUserAndParticipantsIfNeeded(store) {
	const loggedInUser = store.state.risolto.loggedInUser;
	if (loggedInUser) {
		return;
	}

	const { data } = await getUserInfo();

	await loadAndStoreUserAndParticipants(apolloClient, data.sub, store);
}

export const constantRoutes = [
	{
		path: '/redirect',
		component: Layout,
		hidden: true,
		children: [
			{
				path: '/redirect/:path*',
				component: () => import('@/views/redirect/index.vue')
			}
		]
	},
	{
		path: '/welcome',
		hidden: true,
		component: Layout,
		children: [
			{
				path: 'index',
				component: () => import('@/views/_dev/Welcome.vue'),
				name: 'sandbox',
				meta: {
					title: 'routerTitles.Sandbox',
					icon: 'eye', affix: true
				}
			}
		]
	},
	{
		path: '/cognito-login',
		name: 'cognito-login',
		beforeEnter(to, from, next) {
			cognito.login();
		}
	},
	{
		path: '/login/oauth2/code/cognito',
		component: Welcome,
		async beforeEnter(to, from, next) {
			try {
				const redirect_uri = await cognito.handleCognitoResponseWithAuthProxy(window.location.search);
				await LoadPermissionsIfNeeded(store);
				await loadUserAndParticipantsIfNeeded(store);
				if (redirect_uri === null || redirect_uri === '/logout') {
					const participant = store.getters.selectedParticipantStore;
					next({ name: 'worklist', params: { participant } });
				} else {
					router.push(redirect_uri);
				}
				Message.success(i18n.t('loginSuccessful'));
			} catch (e) {
				Message.error(i18n.t('somethingWentWrong'));
				cognito.logout();
			}
		}
	},
	{
		path: '/login',
		component: () => import('@/views/login/index.vue'),
		hidden: true
	},
	{
		path: '/auth-redirect',
		component: () => import('@/views/login/auth-redirect.vue'),
		hidden: true
	},
	{
		path: '/404',
		component: () => import('@/views/error-page/404.vue'),
		hidden: true
	},
	{
		path: '/401',
		component: () => import('@/views/error-page/401.vue'),
		hidden: true
	},
	{
		path: '/exact-redirect',
		component: () => import('@/views/exact-redirect/index.vue'),
		name: 'exact-redirect',
		hidden: true
	},
	{
		path: '/',
		component: Layout,
		redirect: to => {
			// the function receives the target route as the argument
			// we return a redirect path/location here.
			return { name: 'dashboard', params: { participant: store.getters.selectedParticipantStore } };
		},
		meta: {
			required_licence: 'CORE'
		},
		children: [
			/** Backwards compatibility prior to Dec 2024 */
			{
				path: '/dashboard',
				hidden: true,
				redirect: to => {
					return { name: 'dashboard', params: { participant: store.getters.selectedParticipantStore } };
				},
				meta: { title: 'routerTitles.Dashboard', icon: 'dashboard', affix: true }
			},
			{
				path: ':participant/dashboard',
				hidden: true,
				redirect: to => {
					return { name: 'dashboard', params: { participant: store.getters.selectedParticipantStore } };
				},
				meta: { title: 'routerTitles.Dashboard', icon: 'dashboard', affix: true }
			},
			{
				path: ':participant/dashboard/index',
				component: () => import('@/views/dashboard/index.vue'),
				name: 'dashboard',
				meta: { title: 'routerTitles.Dashboard', icon: 'dashboard', affix: true }
			}
		]
	},
	{
		path: '/:participant/quicksight',
		component: Layout,
		meta: { required_integration: 'AWS_QUICKSIGHT' },
		children: [
			{
				path: '',
				component: () => import('@/views/quicksight/index.vue'),
				name: 'quicksight',
				meta: { title: 'routerTitles.QuickSight', icon: 'chart', required_integration: 'AWS_QUICKSIGHT' }
			}
		]
	},
	{
		path: '/profile',
		component: Layout,
		redirect: '/profile/index',
		hidden: true,
		children: [
			{
				path: 'index',
				component: () => import('@/views/profile/index.vue'),
				name: 'Profile',
				meta: { title: 'routerTitles.Profile', icon: 'user', noCache: true }
			}
		]
	},
	{
		hidden: true,
		path: '/error',
		component: Layout,
		redirect: 'noRedirect',
		name: 'ErrorPages',
		meta: {
			title: 'Error',
			icon: '404'
		},
		children: [
			{
				path: '401',
				component: () => import('@/views/error-page/401.vue'),
				name: 'Page401',
				meta: { title: '401', noCache: true }
			},
			{
				path: '404',
				component: () => import('@/views/error-page/404.vue'),
				name: 'Page404',
				meta: { title: '404', noCache: true }
			}
		]
	}
];

/**
 * asyncRoutes
 * the routes that need to be dynamically loaded based on user roles
 */

export const asyncRoutes = [
	/** Backwards compatibility prior to Dec 2024 */
	{
		path: '/worklist',
		component: Layout,
		hidden: true,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				hidden: true,
				component: () => import('@/views/_worklist/index.vue'),
				meta: {
					title: 'routerTitles.Worklist',
					icon: 'tasks-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/invoice-disputes',
		component: Layout,
		hidden: true,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				hidden: true,
				component: () => import('@/views/_invoice-disputes/index.vue'),
				meta: {
					title: 'routerTitles.OnHold',
					icon: 'bug',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: 'recipient',
				hidden: true,
				component: () => import('@/views/_invoice-disputes/recipient.vue'),
				props: (route) => ({
					legalEntity: route.query.legalEntity,
					recipient: route.query.recipient
				}),
				meta: {
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/recipient/',
		component: Layout,
		hidden: true,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_recipient/index.vue'),
				props: (route) => ({ search: route.query.search }),
				hidden: true,
				meta: {
					title: 'routerTitles.recipient',
					icon: 'address-card-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/recipient/',
		component: Layout,
		hidden: true,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: ':id/index',
				component: () => import('@/views/_recipient/detail.vue'),
				props: (route) => ({ recipientRef: route.params.id }),
				hidden: true,
				meta: {
					title: 'routerTitles.recipient',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/invoices',
		component: Layout,
		hidden: true,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				hidden: true,
				component: () => import('@/views/_invoices/index.vue'),
				meta: {
					title: 'routerTitles.Invoices',
					icon: 'file-invoice-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: ':reference/index',
				component: () => import('@/views/_invoices/InvoiceDetail.vue'),
				hidden: true,
				meta: {
					title: 'routerTitles.Invoices',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/credit-notes/:reference',
		hidden: true,
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_credit_notes/detail.vue'),
				meta: {
					title: 'routerTitles.CreditNotes',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/debit-notes/:reference',
		hidden: true,
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_debit_notes/detail.vue'),
				meta: {
					title: 'routerTitles.DebitNotes',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/payment-documents/:reference',
		hidden: true,
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_payment_documents/detail.vue'),
				meta: {
					title: 'routerTitles.PaymentDocuments',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/reports',
		component: Layout,
		hidden: true,
		meta: {
			required_licence: 'REPORTING',
			required_permission: 'UI_VIEW_ADVANCED_REPORTS'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_reports/index.vue'),
				hidden: true,
				meta: {
					title: 'routerTitles.Reports',
					icon: 'excel',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/integrations',
		component: Layout,
		hidden: true,
		meta: {
			required_licence: 'CORE',
			required_permission: 'UI_VIEW_INTEGRATIONS'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_integrations/index.vue'),
				hidden: true,
				meta: {
					title: 'routerTitles.Integrations',
					icon: 'cogs-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/bulk-payments',
		component: Layout,
		hidden: true,
		meta: {
			required_licence: 'PAYMENT_SPLITTING'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_payment-transfer/index.vue'),
				hidden: true,
				meta: {
					title: 'routerTitles.BulkPayments',
					icon: 'cogs-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: ':legalEntity/payment-transfer-details/:paymentTransfer',
				hidden: true,
				component: () => import('@/views/_payment-transfer/payment-transfer-details.vue'),
				props: (route) => ({
					legalEntity: route.query.legalEntity,
					paymentTransfer: route.query.recipient
				}),
				meta: {
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/worklist',
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_worklist/index.vue'),
				name: 'worklist',
				meta: {
					title: 'routerTitles.Worklist',
					icon: 'tasks-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/invoice-disputes/',
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_invoice-disputes/index.vue'),
				meta: {
					title: 'routerTitles.OnHold',
					icon: 'bug',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: 'recipient',
				hidden: true,
				name: 'invoice-disputes.recipient',
				component: () => import('@/views/_invoice-disputes/recipient.vue'),
				props: (route) => ({
					legalEntity: route.query.legalEntity,
					recipient: route.query.recipient
				}),
				meta: {
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/payment-plans',
		component: Layout,
		meta: {
			required_licence: 'CORE',
			required_permission: 'UI_MANAGE_PAYMENT_PLANS'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_payment-plan/index.vue'),
				name: 'payment-plan',
				meta: {
					title: 'routerTitles.PaymentPlan',
					icon: 'calendar-check-regular',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: 'create',
				name: 'create-payment-plan',
				hidden: true,
				component: () => import('@/views/_payment-plan/create.vue'),
				props: (route) => ({ recipient: route.query.recipient }),
				meta: {
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: ':id/:recipient',
				component: () => import('@/views/_payment-plan/edit.vue'),
				name: 'edit-payment-plan',
				hidden: true,
				props: (route) => ({ paymentPlan: route.params.id, recipient: route.params.recipient }),
				meta: {
					title: 'routerTitles.PaymentPlan',
					icon: 'calendar-check-regular',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/messages',
		component: Layout,
		meta: {
			required_licence: 'MAILBOX'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_messages/index.vue'),
				name: 'messages',
				meta: {
					title: 'routerTitles.Messaging',
					icon: 'comments-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: 'recipient',
				hidden: true,
				component: () => import('@/views/_messages/recipient.vue'),
				name: 'messages.recipient',
				meta: {
					title: 'routerTitles.Messaging',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/recipients/',
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_recipient/index.vue'),
				props: (route) => ({ search: route.query.search }),
				name: 'recipients',
				meta: {
					title: 'routerTitles.recipient',
					icon: 'address-card-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: ':id/index',
				component: () => import('@/views/_recipient/detail.vue'),
				props: (route) => ({ recipientRef: route.params.id }),
				name: 'recipient-detail',
				hidden: true,
				meta: {
					title: 'routerTitles.recipient',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: ':id/messages',
				component: () => import('@/views/_recipient/messages.vue'),
				props: (route) => ({ recipientRef: route.params.id }),
				name: 'recipient-messages',
				hidden: true,
				meta: {
					title: 'routerTitles.recipient',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: ':id/archive',
				component: () => import('@/views/_recipient/archive.vue'),
				props: (route) => ({ recipientRef: route.params.id }),
				name: 'recipient-archive',
				hidden: true,
				meta: {
					title: 'routerTitles.recipient',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/invoices/',
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_invoices/index.vue'),
				name: 'invoices',
				meta: {
					title: 'routerTitles.Invoices',
					icon: 'file-invoice-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: ':reference/index',
				component: () => import('@/views/_invoices/InvoiceDetail.vue'),
				name: 'invoice-detail',
				hidden: true,
				meta: {
					title: 'routerTitles.Invoices',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/manual-invoice-upload',
		component: Layout,
		hidden: true,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_invoices/ManualInvoiceUpload.vue'),
				name: 'manual-invoice-upload',
				meta: {
					title: 'routerTitles.Invoices',
					icon: '',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/credit-notes/:reference',
		hidden: true,
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_credit_notes/detail.vue'),
				name: 'credit-note-detail',
				meta: {
					title: 'routerTitles.CreditNotes',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/debit-notes/:reference',
		hidden: true,
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_debit_notes/detail.vue'),
				name: 'debit-note-detail',
				meta: {
					title: 'routerTitles.DebitNotes',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/payment-documents/:reference',
		hidden: true,
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_payment_documents/detail.vue'),
				name: 'payment-document-detail',
				meta: {
					title: 'routerTitles.PaymentDocuments',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/reports',
		component: Layout,
		meta: {
			required_licence: 'REPORTING',
			required_permission: 'UI_VIEW_ADVANCED_REPORTS'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_reports/index.vue'),
				name: 'reports',
				meta: {
					title: 'routerTitles.Reports',
					icon: 'excel',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/legal-entity',
		hidden: process.env.VUE_APP_ENVIRONMENT === 'production' && process.env.VUE_APP_BETA !== 'true',
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_legal-entity/index.vue'),
				name: 'legal-entity',
				meta: {
					title: 'routerTitles.LegalEntities',
					icon: 'store-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/legal-entity/:id',
		hidden: true,
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_legal-entity/LegalEntityDetail.vue'),
				name: 'legal-entity-detail',
				meta: {
					title: 'routerTitles.LegalEntities',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},

	{
		path: '/:participant/templates',
		component: Layout,
		hidden: process.env.VUE_APP_ENVIRONMENT === 'production' && process.env.VUE_APP_BETA !== 'true',
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_templates/index.vue'),
				name: 'templates',
				meta: {
					title: 'routerTitles.Templates',
					icon: 'file-code-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/templates/:id',
		hidden: true,
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_templates/Detail.vue'),
				name: 'template-detail',
				meta: {
					title: 'routerTitles.Template',
					icon: 'file-code-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/integrations',
		component: Layout,
		meta: {
			required_licence: 'CORE',
			required_permission: 'UI_VIEW_INTEGRATIONS'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_integrations/index.vue'),
				name: 'integrations',
				meta: {
					title: 'routerTitles.Integrations',
					icon: 'cogs-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/bulk-payments',
		component: Layout,
		meta: {
			required_licence: 'PAYMENT_SPLITTING'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_payment-transfer/index.vue'),
				name: 'bulk-payments',
				meta: {
					title: 'routerTitles.BulkPayments',
					icon: 'cogs-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			},
			{
				path: ':legalEntity/payment-transfer-details/:paymentTransfer',
				hidden: true,
				name: 'payment-transfer-details',
				component: () => import('@/views/_payment-transfer/payment-transfer-details.vue'),
				props: (route) => ({
					legalEntity: route.query.legalEntity,
					paymentTransfer: route.query.recipient
				}),
				meta: {
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/:participant/reference-data',
		component: Layout,
		meta: {
			required_licence: 'REFERENCE_DATA',
			required_permission: 'REFERENCE_DATA_VIEW'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_reference-data/index.vue'),
				name: 'reference-data',
				meta: {
					title: 'routerTitles.ReferenceData',
					icon: 'cogs-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},
	{
		path: '/settings',
		hidden: true,
		component: Layout,
		meta: {
			required_licence: 'CORE'
		},
		children: [
			{
				path: 'index',
				component: () => import('@/views/_settings/index.vue'),
				name: 'settings',
				meta: {
					title: 'routerTitles.Settings',
					icon: 'cogs-solid',
					noCache: true,
					roles: ['user', 'admin', 'risolto-operator', 'superadmin']
				}
			}
		]
	},

	// 404 page must be placed at the end !!!
	{ path: '*', redirect: '/404', hidden: true }
];

const createRouter = () =>
	new Router({
		mode: 'history', // require service support
		// @ts-ignore
		scrollBehavior: () => ({ y: 0 }),
		routes: constantRoutes
	});

const router = createRouter();

// Detail see: https://github.com/vuejs/vue-router/issues/1234#issuecomment-357941465
export function resetRouter() {
	const newRouter = createRouter();
	// @ts-ignore
	router.matcher = newRouter.matcher; // reset router
}

router.beforeEach(async (to, from, next) => {
	if (to.params.participant != null && to.params.participant !== store.getters.selectedParticipantStore) {
		store.commit('risolto/SET_SELECTED_PARTICIPANT', to.params.participant);
		store.commit('risolto/SET_SELECTED_LEGAL_ENTITY', null); // We don't have the legal entities at this point
	}

	next();
});

export default router;
